import type { NavigateFunction } from 'react-router';

import { routePaths } from '@/routes/routeConfig';
import { getPortalAppDefinition, navigationConstants } from '@/shared/constants';
import type { NavItem } from '@/shared/types';
import { parsePathnameSegments } from '@/shared/utils/path';

const MORGAN_LEWIS_LOGO_ALT = 'Morgan, Lewis & Bockius LLP Logo';

type PortalHomeNavigationArgs = {
  navigate: NavigateFunction;
  target?: EventTarget | null;
  onNavigate?: () => void;
};

/**
 * Single source of truth for AppBar "go home" behavior.
 *
 * - With no `target`, it always navigates to root.
 * - With `target`, it navigates only when the click originated from the
 *   Morgan Lewis logo element.
 */
export const handlePortalHomeNavigation = ({ navigate, target, onNavigate }: PortalHomeNavigationArgs): boolean => {
  if (target != null) {
    if (!(target instanceof HTMLElement)) return false;

    const closestImage = target.closest('img');
    if (!closestImage) return false;
    if (closestImage.getAttribute('alt') !== MORGAN_LEWIS_LOGO_ALT) return false;
  }

  onNavigate?.();
  void navigate(routePaths.root);
  return true;
};

export const getCurrentNavIndex = (pathname: string, navItems?: NavItem[]) => {
  const exactNavMatchIndex = navItems?.findIndex((item) => pathname === item.path) ?? -1;

  const startsWithNavMatchIndex =
    navItems
      ?.map((item, index) => ({ index, path: item.path }))
      .filter((item) => pathname.startsWith(item.path))
      .sort((a, b) => b.path.length - a.path.length)[0]?.index ?? -1;

  return exactNavMatchIndex >= 0 ? exactNavMatchIndex : startsWithNavMatchIndex;
};

export const resolvePortalAppBarTitle = (pathname: string): string => {
  const segments = parsePathnameSegments(pathname);

  if (segments.length === 0) {
    return navigationConstants.homeTitle;
  }

  return getPortalAppDefinition(segments[0])?.title ?? navigationConstants.homeTitle;
};
